/**
 * Module-level store for pre-parsed CSV attachments.
 *
 * CSV files attached by the user are parsed at send-time and stored here
 * so that the `pasteAttachedCsvToSheet` tool can write them directly to
 * the sheet without the model having to relay every cell value.
 */

export interface CsvAttachment {
  filename: string
  rows: (string | number | null)[][]
  rowCount: number
  columnCount: number
}

let csvAttachments: CsvAttachment[] = []

export function setCsvAttachments(attachments: CsvAttachment[]) {
  csvAttachments = attachments
}

export function getCsvAttachment(index: number): CsvAttachment | null {
  return csvAttachments[index] ?? null
}

export function getCsvAttachmentCount(): number {
  return csvAttachments.length
}

export function clearCsvAttachments() {
  csvAttachments = []
}

/**
 * Parse raw CSV text into a 2D array of values.
 * Handles quoted fields and basic type coercion (numbers).
 */
export function parseCsvText(text: string): (string | number | null)[][] {
  const lines = text.split('\n')
  const rows: (string | number | null)[][] = []

  for (const line of lines) {
    if (!line.trim()) continue

    const values: (string | number | null)[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote inside quoted field
          current += '"'
          i++
        } else {
          inQuotes = !inQuotes
        }
      } else if (char === ',' && !inQuotes) {
        values.push(coerceValue(current))
        current = ''
      } else {
        current += char
      }
    }
    values.push(coerceValue(current))
    rows.push(values)
  }

  return rows
}

function coerceValue(raw: string): string | number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  // Only coerce if it looks like a plain number (not a zip code like "01234")
  if (/^-?\d+(\.\d+)?$/.test(trimmed) && !trimmed.startsWith('0')) {
    return Number(trimmed)
  }
  // Allow "0" itself
  if (trimmed === '0') return 0
  return trimmed
}
